import { Box } from '@mui/system';
import { useConfig } from '@revenuewell/logic-config';
import { useDesktop } from '@revenuewell/logic-desktop';
import { useUrl } from '@revenuewell/logic-desktop-oidc';
import { useOidcWithAdmin } from '@revenuewell/logic-oidc';
import { useProduct } from '@revenuewell/logic-product';
import { useProgress } from '@revenuewell/logic-progress';
import { useNotifications } from '@revenuewell/uc-notifications-client';
import { PhoneInUseBanner } from '@revenuewell/widget-phone';
import { CalendarAppProvider } from 'apps/wrapper/src/hooks/use-calendar-app';
import { FormsAppProvider } from 'apps/wrapper/src/hooks/use-forms-app';
import { useLocations } from 'apps/wrapper/src/hooks/use-locations';
import { useProductsByLocation } from 'apps/wrapper/src/hooks/use-products-by-location';
import { useEffect, useState } from 'react';
import { Route } from 'react-router';
import { useEventBus } from '../../../hooks/use-event-bus';
import { useHistory } from '../../../hooks/use-history-intercept';
import { SlideoutProvider } from '../../../hooks/use-slideout';
import { useViewMode } from '../../../hooks/use-view-mode';
import WrapperBusProvider from '../../../hooks/use-wrapper-bus/provider';
import { WrapperFramesProvider, useFrames } from '../../../hooks/use-wrapper-frames';
import { WrapperNavigationProvider } from '../../../hooks/use-wrapper-navigation';
import { PageRouter } from '../../../routing/page-router/page-router';
import { ContactApi } from '../../../services/api-client/contact-api';
import { VoiceApi } from '../../../services/api-client/voice-api';
import { EventFileDownloadOrPreview } from '../../../types/event-bus';
import { DevTools } from '../../dev/dev-tools/dev-tools';
import Menu from '../../pages/menu/menu';
import { LoadingLocationModal } from '../../shared/loading-location-modal/loading-location-modal';
import { MfaDialog } from '../../shared/mfa-dialog/mfa-dialog';
import { SmsRegistartion } from '../../shared/sms-registration/sms-registration';
import { VirtualVisitInvite } from '../../shared/virtual-visit-invite/virtual-visit-invite';
import FormsModals from '../slideout/forms-widget/forms-modals';
import { MilaCaseModal } from '../slideout/mila-widget/mila-case-modal';
import SlideOut from '../slideout/slideout';
import { ComposeSocialMediaEventListener } from './compose-social-media-event-listener';
import { ReviewSummaryEventListener } from './review-summary-event-listener';
import { ConnectFacebookEventListener } from './connect-facebook-event-listener';
import { EventBusCopyToClipboardListener } from './event-bus-copy-to-clipboard-listener';
import { EventBusNotificationMessageListener } from './event-bus-notification-message-listener';
import { FormsNotificationEventListener } from './forms-notification-event-listenter';
import Frames from './frames';
import { ViewEmailCommunicationListener } from './listeners/communication/view-email-communication-listener';
import { PhoneLoader } from './loaders/phone/phone-loader';
import { ColumnCenter, ColumnLeft, Container } from './main.styles';
import { NotificationEventListener } from './notification-event-listenter';
import { TerminateAuthSessionListener } from './terminate-auth-session-listener';
import { TopNav } from './top-nav';

const Main = () => {
  const { heightMode } = useViewMode();
  const isShort = heightMode === 'Short';
  const isMedium = heightMode === 'Medium';
  const { listen, publish } = useEventBus();
  const { listen: listenNotification } = useNotifications();
  const { hideAppSpinner } = useProgress();
  const { allFramesLoaded } = useFrames();
  const { selectedLocation, selectedLocationWithStatus, updateSelectedLocationWithStatus } = useLocations();
  const { isDesktopApplication, downloadFile } = useDesktop();
  const { openUrl } = useUrl({ web: 'NewTab', desktop: 'DefaultBrowser' });
  const { hasProduct, isLoading: isLoadingProducts } = useProductsByLocation(selectedLocation?.id);

  useEffect(() => {
    if (allFramesLoaded) hideAppSpinner();

    if (
      allFramesLoaded &&
      selectedLocationWithStatus?.location.id &&
      selectedLocationWithStatus.status === 'InProgress' &&
      !isLoadingProducts
    ) {
      updateSelectedLocationWithStatus({ status: 'Complete' });
    }
  }, [allFramesLoaded, selectedLocationWithStatus, isLoadingProducts]);

  useEffect(
    () =>
      listen('fileDownloadOrPreview', (event) => {
        const { data } = event as EventFileDownloadOrPreview;

        if (isDesktopApplication()) {
          downloadFile(data.url);
        } else {
          openUrl(data.url);
        }
      }),
    []
  );

  useEffect(
    () =>
      listenNotification('m2-message', (event: any) => {
        if (!event.locationid && !event.locationId) return;

        const data = event.data as MessageEvent['data'];

        if (data?.channel?.includes('insurance-mfa')) {
          publish({
            messageType: 'mfaCodesWidget',
            data: {
              locationId: event.locationid || event.locationId
            }
          });
        }
      }),
    [listenNotification]
  );

  useEffect(
    () =>
      listenNotification('insurance-verification-update', (event: any) => {
        if (!event.locationid && !event.locationId) return;

        console.log('INSURANCE VERIFICATION UPDATE EVENT:', event);
        const data = event.data as MessageEvent['data'];

        if (data?.eventType == 'datasource_waiting_2fa') {
          publish({
            messageType: 'mfaCodeDialog',
            data: {
              locationId: event.locationid || event.locationId,
              entityId: data?.entityId
            }
          });
        }
      }),
    [listenNotification]
  );

  const { viewMode } = useViewMode();
  const isSmall = viewMode === 'Phone';

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <TopNav />
      {!isLoadingProducts && hasProduct('phone2') && <PhoneInUseBanner />}
      <Container>
        {!isSmall && (
          <ColumnLeft className="column-left" isShort={isShort} isMedium={isMedium}>
            <Menu />
          </ColumnLeft>
        )}
        <ColumnCenter>
          <PhoneLoader />
          <Frames />
          <PageRouter />
        </ColumnCenter>
        <SlideOut />
      </Container>
      <SmsRegistartion />
      <MfaDialog />
      <FormsModals />
      <MilaCaseModal />
      <ViewEmailCommunicationListener />
      <ComposeSocialMediaEventListener />
      <ReviewSummaryEventListener />
      <ConnectFacebookEventListener />
      <LoadingLocationModal open={!allFramesLoaded} locationName={selectedLocation?.name} />
      <EventBusNotificationMessageListener />
      <EventBusCopyToClipboardListener />
      <TerminateAuthSessionListener />
      <DevTools />
    </Box>
  );
};

export default () => {
  const history = useHistory();
  const { config } = useConfig();
  const { showAppSpinner } = useProgress();
  const { hasProduct } = useProduct();
  const { getClaims, oidcService } = useOidcWithAdmin();
  const [apiInitialized, setApiInitialized] = useState(false);
  const isRootPath = history.location.pathname === '/';

  useEffect(() => {
    showAppSpinner();
  }, []);

  useEffect(() => {
    (async () => {
      const claims = await getClaims();

      const pathname = history.location.pathname;

      if (pathname === '/') {
        const corePath = claims.userClass === 'GlobalUser' ? '/core/Auth/SignInForOthers' : '/core';

        if (hasProduct('core') || hasProduct('reminders')) history.push(corePath);
        else if (hasProduct('/messenger2')) history.push('/messenger');
        else history.push('/messenger1');
      }

      const firstSegment = pathname.split('/')[1] || null;
      const isPhone1Path = firstSegment === 'phone1';
      const isPhone2Path = firstSegment === 'phone';

      if (isPhone1Path && !(hasProduct('phone') || hasProduct('phone1'))) {
        history.push('/');
      }

      if (isPhone2Path && !hasProduct('phone2')) {
        history.push('/');
      }
    })();
  }, [history, hasProduct]);

  useEffect(() => {
    if (!config || !oidcService) return;

    VoiceApi.initialize(config, oidcService);
    ContactApi.initialize(config, oidcService);
    setApiInitialized(true);
  }, [config, oidcService]);

  return (
    <>
      {!isRootPath && apiInitialized && (
        <>
          <Route path="/:frameName">
            <WrapperNavigationProvider>
              <SlideoutProvider>
                <WrapperBusProvider>
                  <WrapperFramesProvider>
                    <NotificationEventListener>
                      <FormsNotificationEventListener>
                        <CalendarAppProvider>
                          <FormsAppProvider>
                            <Main />
                          </FormsAppProvider>
                        </CalendarAppProvider>
                      </FormsNotificationEventListener>
                    </NotificationEventListener>
                  </WrapperFramesProvider>
                </WrapperBusProvider>
              </SlideoutProvider>
            </WrapperNavigationProvider>
          </Route>
          <VirtualVisitInvite />
        </>
      )}
    </>
  );
};
